/**
 * 旅游线路订单相关接口
 * 对应 Home端 - 旅游线路订单 模块
 */
import { http } from "@/http/axios";

// ==================== 类型定义 ====================

/** 出行人类型：1=成人, 2=儿童, 3=老人, 4=幼儿 */
export type TravelerType = 1 | 2 | 3 | 4;

// ==================== 预创建订单 ====================

/** 默认价格（成人/儿童/老人/幼儿） */
export interface DefaultPrice {
  adult_price: number;
  child_price: number;
  senior_price: number;
  infant_price: number;
}

/** 日历日期价格项 */
export interface CalendarDateItem {
  date: string;
  adult_price: number;
  child_price: number;
  senior_price: number;
  infant_price: number;
  stock: number;
  is_available: boolean;
}

/** 预创建 - 取消政策项 */
export interface PreCancelPolicyItem {
  day: number;
  text: string;
  percent: number;
}

/** 预创建 - 产品信息 */
export interface PreCreateProductInfo {
  id: number;
  title: string;
  subtitle: string;
  cover_image: string;
  category_name: string;
  days: number;
  nights: number;
  destinations: string;
  departure_city: string;
  travel_type: number;
  travel_type_text: string;
  min_people: number;
  max_people: number;
  default_price: DefaultPrice;
  cancel_policy: PreCancelPolicyItem[];
}

/** 预创建 - 预订配置 */
export interface BookingConfig {
  min_date: string;
  max_date: string;
  advance_book_days: number;
  min_people: number;
  max_people: number;
}

/** 预创建订单响应 data（产品信息+日历+配置） */
export interface PreCreateOrderData {
  product: PreCreateProductInfo;
  calendar_list: CalendarDateItem[];
  booking_config: BookingConfig;
}

/** 预创建订单请求参数 */
export interface PreCreateOrderParams {
  product_id: number;
}

/** 预创建订单响应 */
export interface PreCreateOrderResponse {
  code: number;
  message: string;
  data: PreCreateOrderData;
}

// ==================== 确认订单 ====================

/** 出行人信息（提交时） */
export interface TravelerItem {
  name: string;
  type: TravelerType;
  phone?: string;
  id_card?: string;
}

/** 确认订单请求参数 */
export interface ConfirmOrderParams {
  order_no?: string;
  product_id: number;
  departure_date: string;
  adult_count: number;
  child_count: number;
  senior_count: number;
  infant_count: number;
  adult_price: number;
  child_price?: number;
  senior_price?: number;
  infant_price?: number;
  contact_name: string;
  contact_phone: string;
  contact_email?: string;
  travelers: TravelerItem[];
  special_requests?: string[];
}

/** 价格明细（单价+数量+金额） */
export interface PeoplePriceDetail {
  count: number;
  unit_price: number;
  amount: number;
}

/** 人数价格汇总 */
export interface PeoplePriceInfo {
  adult: PeoplePriceDetail;
  child: PeoplePriceDetail;
  senior: PeoplePriceDetail;
  infant: PeoplePriceDetail;
}

/** 联系人信息 */
export interface ContactInfo {
  name: string;
  phone: string;
  email: string;
}

/** 确认后出行人（含id和type_text） */
export interface ConfirmedTraveler {
  id: number;
  name: string;
  type: TravelerType;
  type_text: string;
  phone?: string;
  id_card?: string;
}

/** 确认订单 - 取消政策 */
export interface ConfirmCancelPolicyItem {
  day: number;
  text: string;
  percent: number;
}

/** 确认订单响应 data */
export interface ConfirmOrderData {
  order_id: number;
  order_no: string;
  product: {
    id: number;
    title: string;
    subtitle: string;
    cover_image: string;
    category_name: string;
  };
  departure_date: string;
  days: number;
  nights: number;
  destinations: string;
  departure_city: string;
  travel_type: number;
  travel_type_text: string;
  people: PeoplePriceInfo;
  total_people: number;
  total_amount: number;
  contact: ContactInfo;
  travelers: ConfirmedTraveler[];
  cancel_policy: ConfirmCancelPolicyItem[];
}

/** 确认订单响应 */
export interface ConfirmOrderResponse {
  code: number;
  message: string;
  data: ConfirmOrderData;
}

// ==================== 订单详情 ====================

/** 行程安排项（复用 tour.ItineraryDay） */
export type ItineraryItem = import("./tour").ItineraryDay;

/** 订单详情 - 产品信息 */
export interface OrderProductInfo {
  id: number;
  product_no: string;
  title: string;
  subtitle: string;
  cover?: string; // 商品封面图
  cover_image?: string;
  category_id: number;
  category_name: string;
  itinerary?: ItineraryItem[]; // 行程安排
}

/** 订单详情 - 人数统计 */
export interface OrderPeopleCount {
  adult: number;
  child: number;
  senior: number;
  infant: number;
  total: number;
}

/** 订单详情 - 价格明细 */
export interface OrderPriceDetail {
  adult: { unit: string; count: number; amount: string };
  child: { unit: string; count: number; amount: string };
  senior: { unit: string; count: number; amount: string };
  infant: { unit: string; count: number; amount: string };
}

/** 订单详情 - 取消政策 */
export interface DetailCancelPolicyItem {
  day: number;
  percent: number;
  description: string;
}

/** 订单状态枚举 */
export type TourOrderStatus =
  | 0 // 待支付
  | 1 // 已支付 / 待出行
  | 2 // 已出行
  | 3 // 已完成
  | 4 // 已取消
  | 5; // 已退款

/** 订单详情响应 data */
export interface TourOrderDetail {
  id: number;
  order_no: string;
  product: OrderProductInfo;
  departure_date: string;
  days: number;
  nights: number;
  has_comment: number; // 1=已评论 0=未评论
  trip_description: string;
  destinations: string;
  departure_city: string;
  travel_type: number;
  travel_type_text: string;
  people: OrderPeopleCount;
  contact: ContactInfo;
  special_requests: string[];
  travelers: ConfirmedTraveler[];
  price: OrderPriceDetail;
  total_amount: string;
  cancel_policy: DetailCancelPolicyItem[];
  refund_amount: string;
  refund_reason: string;
  refund_time: string | null;
  status: TourOrderStatus;
  status_text: string;
  pay_type: number;
  pay_type_text: string;
  pay_time: string;
  transaction_id: string;
  add_time: string;
  update_time: string;
  refund_info?: {
    has_refund: number;
    refund_id: number;
    refund_no: string;
    refund_status: number;
    refund_status_text: string;
    apply_desc: string;
    apply_img: string[];
    apply_reason: string;
    create_time: string;
    refund_money: string;
    refuse_desc: string;
  };
}

/** 订单详情响应 */
export interface TourOrderDetailResponse {
  code: number;
  message: string;
  data: TourOrderDetail;
}

// ==================== 取消订单 ====================

/** 取消订单请求参数 */
export interface CancelOrderParams {
  id: number;
  reason: string;
}

/** 取消订单响应 data */
export interface CancelOrderData {
  order_no: string;
  refund_amount: string;
  status: number;
  status_text: string;
}

/** 取消订单响应 */
export interface CancelOrderResponse {
  code: number;
  message: string;
  data: CancelOrderData;
}

// ==================== 订单列表 ====================

/** 订单列表项 */
export interface TourOrderListItem {
  id: number;
  order_no: string;
  title: string;
  cover: string;
  departure_date: string;
  trip_description: string;
  total_people: number;
  total_amount: string;
  status: TourOrderStatus;
  status_text: string;
  add_time: string;
}

/** 订单列表请求参数 */
export interface TourOrderListParams {
  page?: number;
  limit?: number;
  status?: TourOrderStatus;
}

/** 订单列表响应 */
export interface TourOrderListResponse {
  code: number;
  message: string;
  data: {
    list: TourOrderListItem[];
    total: number;
    page: number;
    limit: number;
  };
}

// ==================== 接口请求 ====================

/**
 * 预创建旅游线路订单（获取产品信息和可选日期日历）
 * @param params.product_id 产品ID
 */
export function preCreateTourOrder(params: PreCreateOrderParams) {
  return http.post<PreCreateOrderResponse>(
    "/home/tour-order/pre-create",
    params,
  );
}

/**
 * 确认旅游线路订单
 * 创建或更新订单，保存联系人信息和出行人员信息
 */
export function confirmTourOrder(params: ConfirmOrderParams) {
  return http.post<ConfirmOrderResponse>("/home/tour-order/confirm", params);
}

/**
 * 获取旅游线路订单详情
 * @param id 订单ID
 */
export function getTourOrderDetail(id: number) {
  return http.get<TourOrderDetailResponse>("/home/tour-order/detail", {
    params: { id },
  });
}

/**
 * 取消旅游线路订单
 * 根据取消政策计算退款金额
 */
export function cancelTourOrder(params: CancelOrderParams) {
  return http.post<CancelOrderResponse>("/home/tour-order/cancel", params);
}

/**
 * 获取旅游线路订单列表（支持按状态筛选和分页）
 */
export function getTourOrderList(params: TourOrderListParams = {}) {
  return http.get<TourOrderListResponse>("/home/tour-order/list", { params });
}
